import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { jwtDecode } from "jwt-decode";
import { useLocation, useNavigate } from "react-router-dom";
import contactHttpClient from "../utils/contactHttpClient";

interface ContactAuthContextValue {
  contactToken: string | null;
  loginContact: (
    email: string,
    paymentLinkSlug?: string,
  ) => Promise<{ tokenId: string }>;
  confirmContactCode: (tokenId: string, code: string) => Promise<void>;
  logoutContact: () => void;
  isContactAuthenticated: boolean;
}

const ContactAuthContext = createContext<ContactAuthContextValue>({
  contactToken: null,
  loginContact: async () => {
    throw new Error("loginContact not implemented");
  },
  confirmContactCode: async () => {
    throw new Error("confirmContactCode not implemented");
  },
  logoutContact: () => {
    throw new Error("logoutContact not implemented");
  },
  isContactAuthenticated: false,
});

interface JwtPayload {
  exp?: number;
  [key: string]: any;
}

function decodeJwtExpiry(token: string): number | null {
  try {
    const payload = jwtDecode<JwtPayload>(token);
    return payload.exp ?? null;
  } catch {
    return null;
  }
}

interface ContactAuthProviderProps {
  children: ReactNode;
  redirectOnLogout?: boolean;
}

export const ContactAuthProvider: React.FC<ContactAuthProviderProps> = ({
  children,
  redirectOnLogout = true,
}) => {
  const [contactToken, setContactToken] = useState<string | null>(null);
  const [tokenExpiry, setTokenExpiry] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // On mount, read token from localStorage and validate
  useEffect(() => {
    const savedToken = localStorage.getItem("CONTACT_JWT_TOKEN");
    if (savedToken) {
      const exp = decodeJwtExpiry(savedToken);
      const nowInSeconds = Date.now() / 1000;
      if (exp && exp > nowInSeconds) {
        setContactToken(savedToken);
        setTokenExpiry(exp);
      } else {
        localStorage.removeItem("CONTACT_JWT_TOKEN");
      }
    }
  }, []);

  // Schedule logout when token is about to expire.

  useEffect(() => {
    if (!tokenExpiry) return;
    const now = Date.now() / 1000;
    const msUntilExpiry = (tokenExpiry - now) * 1000;

    if (msUntilExpiry <= 0) {
      // Already expired
      logoutContact();
      return;
    }
    const timeoutId = window.setTimeout(() => {
      logoutContact();
    }, msUntilExpiry);

    return () => window.clearTimeout(timeoutId);
  }, [tokenExpiry]);

  // Retrieves tokenId and sends code to the email address.

  const loginContact = useCallback(
    async (email: string, paymentLinkSlug?: string) => {
      const response = await contactHttpClient.post<{ tokenId: string }>(
        "/api/v1/contact/validate",
        { email, ...(paymentLinkSlug ? { paymentLinkSlug } : {}) },
      );
      return response.data;
    },
    [],
  );

  // Validates token and code matching the email address.

  const confirmContactCode = useCallback(
    async (tokenId: string, code: string) => {
      const response = await contactHttpClient.post<{ jwtToken: string }>(
        "/contact/token",
        { tokenId, code },
        { suppressAuthToast: true },
      );
      const jwtToken = response.data.jwtToken;
      const exp = decodeJwtExpiry(jwtToken);
      if (!exp) {
        throw new Error("Invalid JWT: no exp field");
      }
      // Save to state & localStorage
      setContactToken(jwtToken);
      setTokenExpiry(exp);
      localStorage.setItem("CONTACT_JWT_TOKEN", jwtToken);
    },
    [],
  );

  const logoutContact = useCallback(() => {
    setContactToken(null);
    setTokenExpiry(null);
    localStorage.removeItem("CONTACT_JWT_TOKEN");
    if (redirectOnLogout) {
      navigate("/contact/login", { state: { from: location } });
    }
  }, [navigate, redirectOnLogout]);

  const isContactAuthenticated =
    !!contactToken && (tokenExpiry ?? 0) > Date.now() / 1000;

  return (
    <ContactAuthContext.Provider
      value={{
        contactToken,
        loginContact,
        confirmContactCode,
        logoutContact,
        isContactAuthenticated,
      }}
    >
      {children}
    </ContactAuthContext.Provider>
  );
};

export function useContactAuth(): ContactAuthContextValue {
  return useContext(ContactAuthContext);
}
